import Check from '@mui/icons-material/Check';
import { Box } from '@mui/material';
import { ReactElement, useCallback, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { QueryItemAggregate } from '../../api/schema';
import LinearProgress from '../../component/linearProgress/LinearProgress';
import Table from '../../component/table/Table';
import { tagsDate } from '../../helpers/dataHelper';
import { useIsRemediation } from '../../hooks/useIsRemediation';
import useSearchParams from '../../hooks/useSearchParams';
import useTablePagination from '../../hooks/useTablePagination';
import { getItemsLeftBehindAggById, getItemsLeftBehindAggByIdFetching, listItemsLeftBehindAgg } from '../../redux/slices/tools/toolsSlice';
import { useDispatch } from '../../redux/store';

type ToolsLeftBehindAggregateProps = {
  dense?: boolean;
  title?: string;
  headerAddon?: ReactElement;
  withExport?: boolean;
  defaultPageSize?: number;
};
export const ToolsLeftBehindAggregate: React.FC<ToolsLeftBehindAggregateProps> = ({
  dense,
  title,
  headerAddon,
  withExport,
  defaultPageSize,
}: ToolsLeftBehindAggregateProps) => {
  const dispatch = useDispatch();
  const [params] = useSearchParams();
  // Oldest "Since" first unless the user picked a sort; sent explicitly so the
  // header arrow and the request agree.
  const sortBy = params.sortBy || 'last_visit';
  const fetching = useSelector(getItemsLeftBehindAggByIdFetching);
  const items = useSelector(getItemsLeftBehindAggById({ ...params, sortBy }));
  const [paginationFromParams, paginationToGetFromParams, paginationControl, paginationToGetAll] = useTablePagination({
    paginationDirty: items.pagination,
    defaultLimit: defaultPageSize,
  });
  const pagination = useMemo(() => ({ ...paginationFromParams, sortBy, desc: sortBy.startsWith('-') }), [paginationFromParams, sortBy]);
  const paginationToGet = useMemo(() => ({ ...paginationToGetFromParams, sortBy }), [paginationToGetFromParams, sortBy]);
  const itemsExport = useSelector(getItemsLeftBehindAggById({ ...params, sortBy, ...paginationToGetAll }));
  const isRemediation = useIsRemediation();
  const totals = useMemo(() => ({ tools_left_behind_cnt: items.totals?.tools_left_behind_cnt ?? 0 }), [items.totals]);

  const starredNoteFirstLine = (notes?: string | null): string => {
    if (!notes || !notes.startsWith('*')) return '';
    const firstLine = notes.split('\n')[0];
    return firstLine.replace(/^\*\s*/, '');
  };

  useEffect(() => {
    dispatch(listItemsLeftBehindAgg({ ...params, ...paginationToGet }));
  }, [dispatch, params, paginationToGet]);

  const getExportData = useCallback(async () => {
    await dispatch(
      listItemsLeftBehindAgg({
        ...params,
        sortBy,
        ...paginationToGetAll,
      }),
    );
  }, [dispatch, params, sortBy, paginationToGetAll]);

  const columns = useMemo(
    () => [
      {
        Header: 'Job Site',
        accessor: 'site_name',
        ellipsis: true,
        // Narrow name, project manager and location so the note gets the room.
        width: '20%',
        minWidth: 120,
        Cell: ({ row }: { row: { original: QueryItemAggregate } }) => (
          <Link to={`/customers/job-sites/${row.original.site_id}/details`}>{row.original.site_name}</Link>
        ),
      },
      {
        // The client's own reference for the job; optional, so most rows are blank.
        Header: 'Job #',
        accessor: 'job_number',
        width: 90,
        Cell: ({ row }: { row: { original: QueryItemAggregate } }) => row.original.job_number || '',
      },
      ...(isRemediation
        ? [
            {
              Header: 'Project Manager',
              accessor: 'project_manager',
              ellipsis: true,
              width: '14%',
              minWidth: 70,
              Cell: ({ row }: { row: { original: QueryItemAggregate } }) => row.original.project_manager ?? '',
              getCellExportValue: (row: { original: QueryItemAggregate }) => row.original.project_manager ?? '',
            },
          ]
        : []),
      ...(isRemediation
        ? [
            {
              Header: 'Note',
              accessor: 'notes',
              ellipsis: true,
              width: '66%',
              minWidth: 200,
              Cell: ({ row }: { row: { original: QueryItemAggregate } }) => starredNoteFirstLine(row.original.notes),
              getCellExportValue: (row: { original: QueryItemAggregate }) => starredNoteFirstLine(row.original.notes),
            },
          ]
        : []),
      {
        Header: 'Location',
        accessor: 'address',
        ellipsis: true,
        width: '25%',
        minWidth: 110,
        Cell: ({ row }: { row: { original: QueryItemAggregate } }) => [row.original.city, row.original.state].filter(Boolean).join(', '),
        getCellExportValue: (row: { original: QueryItemAggregate }) => [row.original.city, row.original.state].filter(Boolean).join(', '),
      },
      {
        Header: 'Since',
        accessor: 'last_visit',
        width: '15%',
        minWidth: 92,
        ellipsis: true,
        Cell: ({ row }: { row: { original: QueryItemAggregate } }) => tagsDate(row.original.last_visit, 'DATE_MED', 'DATE_MED'),
        getCellExportValue: (row: { original: QueryItemAggregate }) => tagsDate(row.original.last_visit, 'DATE_MED', 'DATE_MED'),
      },
      ...(isRemediation
        ? [
            {
              Header: 'Ready For Pickup',
              accessor: 'pickup_date',
              width: '15%',
              minWidth: 110,
              ellipsis: true,
              // Compound: a check when flagged ready, then the scheduled pickup date if set.
              Cell: ({ row }: { row: { original: QueryItemAggregate } }) => {
                const ready = row.original.ready_for_pickup === true;
                const date = row.original.pickup_date ? tagsDate(row.original.pickup_date, 'DATE_MED', 'DATE_MED') : '';
                return (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {ready && <Check fontSize="small" color="success" />}
                    {date && <span>{date}</span>}
                  </Box>
                );
              },
              getCellExportValue: (row: { original: QueryItemAggregate }) => {
                // Mirror the on-screen compound cell: a check when flagged ready,
                // joined by "/" with the scheduled date when both are present.
                const ready = row.original.ready_for_pickup === true;
                const date = row.original.pickup_date ? tagsDate(row.original.pickup_date, 'DATE_MED', 'DATE_MED') : '';
                if (ready && date) return `✓ / ${date}`;
                if (ready) return '✓';
                return date;
              },
            },
          ]
        : []),
      {
        Header: 'Count',
        accessor: 'tools_left_behind_cnt',
        width: '10%',
        style: {
          textAlign: 'center',
        },
      },
    ],
    [isRemediation],
  );

  return (
    <>
      {fetching && <LinearProgress />}

      <Table
        title={title}
        columns={columns}
        data={items.result || []}
        loading={fetching}
        toolbarDense="medium"
        size={dense ? 'small' : 'medium'}
        noGlobalFilter
        paginationControl={paginationControl}
        pagination={pagination}
        totals={totals}
        headerAddon={headerAddon}
        {...(withExport && { dataExport: itemsExport.result, getExportData, exportFileName: 'LeftBehind' })}
      />
    </>
  );
};
